import React, { FC, useCallback, useMemo } from "react"
import { Map } from "immutable"
import { turbine } from "neql"
import { ExclusiveChoiceDropdown, AddButton } from "dash-styled"

import {
    FormFieldsLabelerRestriction,
    ALL_TASKS,
    RestrictionFilter,
    TaskName
} from "../FormFields"
import { RemoveButton } from "../../../../../../../../../../../Common/Button/RemoveButton"
import { FetchState } from "../../../../../../../../../../../../Models/Client/FetchState"
import { LinkButton } from "../../../../../../../../../../../Common/LinkButton/LinkButton"
import { LabelerRestriction } from "./LabelerRestriction"

interface LabelerPoolRestrictionsProps {
    taskNames: string[]
    qualificationMetricNamesFetch: FetchState<string[]>
    emailErrors: Map<TaskName, string>
    value: FormFieldsLabelerRestriction[]
    defaultValue: FormFieldsLabelerRestriction[]
    trainingTests: turbine.TrainingTest[]
    onChange: (value: {
        labelerRestrictions: FormFieldsLabelerRestriction[]
    }) => void
    onSearchQualificationMetricNames: (search: string) => void
    onLoadMoreQualificationMetricNames?: () => void
}

function taskNameToString(taskName: TaskName): string {
    return typeof taskName === "string" ? taskName : "all tasks"
}

export const LabelerPoolRestrictions: FC<LabelerPoolRestrictionsProps> = ({
    taskNames,
    qualificationMetricNamesFetch,
    emailErrors,
    value,
    defaultValue,
    trainingTests,
    onChange,
    onSearchQualificationMetricNames,
    onLoadMoreQualificationMetricNames
}) => {
    const selectableTaskNames = useMemo<TaskName[]>(() => {
        if (taskNames.length === 0) {
            return []
        }

        if (taskNames.length === 1) {
            return [ALL_TASKS]
        }

        return ([ALL_TASKS] as TaskName[]).concat(taskNames)
    }, [taskNames])
    const unusedTaskNames = useMemo(() => {
        const usedTaskNames = value.map(v => v.taskName)

        return selectableTaskNames.filter(t => !usedTaskNames.includes(t))
    }, [value, selectableTaskNames])
    const multiple = selectableTaskNames.length > 1 || value.length > 1
    const handleChange = useCallback(
        (value: FormFieldsLabelerRestriction[]) => {
            onChange({ labelerRestrictions: value })
        },
        [onChange]
    )
    const handleAdd = useMemo(() => {
        if (
            unusedTaskNames.length === 0 ||
            (value.length > 0 && taskNames.length < 2)
        ) {
            return undefined
        }

        const initialValue: FormFieldsLabelerRestriction = {
            taskName: unusedTaskNames[0],
            restriction: []
        }

        return () => handleChange(value.concat([initialValue]))
    }, [unusedTaskNames, taskNames.length, handleChange, value])
    const handleReset = useCallback(
        () => handleChange(defaultValue),
        [defaultValue, handleChange]
    )
    const isDefault = useMemo(
        () => JSON.stringify(value) === JSON.stringify(defaultValue),
        [defaultValue, value]
    )
    const renderLabelerRestriction = useCallback(
        (
            { taskName, restriction }: FormFieldsLabelerRestriction,
            index: number,
            arr: unknown[]
        ) => {
            const handleChangeRestriction = (newV: RestrictionFilter[]) =>
                handleChange(
                    value.flatMap(v =>
                        v.taskName === taskName
                            ? newV.length === 0
                                ? []
                                : [{ taskName, restriction: newV }]
                            : [v]
                    )
                )
            const handleRemove = () =>
                handleChange(value.filter(v => v.taskName !== taskName))
            const handleChangeTask = (taskName: TaskName) =>
                handleChange(
                    value.flatMap((v, j) =>
                        index === j
                            ? [{ taskName, restriction }]
                            : v.taskName === taskName
                            ? []
                            : [v]
                    )
                )

            return (
                <div
                    className={`xl:ml-0 ${
                        index === arr.length - 1
                            ? ""
                            : "border-b border-solid border-rhino-300 mb-4"
                    } ${multiple ? "ml-4" : ""}`}
                    data-testid="restriction-group"
                    key={taskName === ALL_TASKS ? 0 : taskName}
                >
                    <div className="flex items-center mb-2">
                        <span>Workers for</span>
                        {selectableTaskNames.length > 1 ||
                        !selectableTaskNames.includes(taskName) ? (
                            <ExclusiveChoiceDropdown
                                className="mx-2 w-48"
                                testId="task-name-chooser"
                                size="sm"
                                choices={selectableTaskNames}
                                value={taskName}
                                renderChoice={taskNameToString}
                                onClick={handleChangeTask}
                            />
                        ) : (
                            <span className="mx-1">
                                {taskNameToString(taskName)}
                            </span>
                        )}
                        <span className="flex-grow">must:</span>
                        {multiple ? (
                            <RemoveButton
                                testId="remove-button"
                                className="text-grape-900"
                                icon="close"
                                onRemove={handleRemove}
                            />
                        ) : null}
                    </div>
                    <LabelerRestriction
                        className={`pl-1 ${
                            multiple ? "ml-12 mr-8 xl:mr-16" : ""
                        }`}
                        value={restriction}
                        qualificationMetricNamesFetch={
                            qualificationMetricNamesFetch
                        }
                        emailError={emailErrors.get(taskName)}
                        onChange={handleChangeRestriction}
                        onSearchQualificationMetricNames={
                            onSearchQualificationMetricNames
                        }
                        onLoadMoreQualificationMetricNames={
                            onLoadMoreQualificationMetricNames
                        }
                        trainingTests={trainingTests}
                    />
                </div>
            )
        },
        [
            multiple,
            selectableTaskNames,
            qualificationMetricNamesFetch,
            emailErrors,
            onSearchQualificationMetricNames,
            onLoadMoreQualificationMetricNames,
            trainingTests,
            handleChange,
            value
        ]
    )

    return (
        <>
            <div>
                {selectableTaskNames.length > 0
                    ? value.map(renderLabelerRestriction)
                    : "There is no task defined"}
            </div>
            {handleAdd !== undefined && (
                <AddButton
                    className="ml-4 xl:ml-0"
                    text="Add restriction group"
                    testId="add-group-button"
                    onClick={handleAdd}
                />
            )}
            {isDefault ? null : (
                <LinkButton
                    testId="reset-labeler-pool-restrictions-button"
                    className="block mt-2 ml-4 xl:ml-0"
                    onClick={handleReset}
                >
                    Reset to defaults
                </LinkButton>
            )}
        </>
    )
}
